import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Zoom,
} from "@mui/material";
import {
  COLOR_BLACK,
  COLOR_LESS_BLACK,
  COLOR_LIGHT_GRAY,
  COLOR_WHITE,
  GRADIENT_GRAY,
} from "../../../../../data/colors/Colors";
import {
  Attachment,
  CheckCircle,
  CloudUploadRounded,
  Delete,
  EditRounded,
  Save,
} from "@mui/icons-material";
import { CustomImageView } from "../../../../component/CustomImageView";
import useWindowDimensions from "../../../../../core/utils/UseWindowDimensions";
import {
  STRINGS_ARE_YOU_SURE_YOU_WANT_TO_DELETE_THE_CATEGORY,
  STRINGS_CATEGORY,
  STRINGS_CREATE,
  STRINGS_DELETE,
  STRINGS_DELETE_CATEGORY,
  STRINGS_EDIT_TITLE,
  STRINGS_SAVE,
  STRINGS_TITLE,
} from "../../../../../data/strings/Strings";
import { useEffect, useRef, useState } from "react";
import AutoHeight from "../../../../component/AutoHeightView";
import { CustomTextField } from "../../../../component/CustomTextField";
import { CustomTextView } from "../../../../component/CustomTextView";
import { HeaderView } from "../../../../component/HeaderView";
import { CustomTitledView } from "../../../../component/CustomTitledView";
import {
  ROUTE_ENDPOINTS,
  ROUTE_NEW,
} from "../../../../../data/constant/RouteConstants";
import { CategoryDetailsViewModel } from "./CategoryDetailsViewModel";
import { FilePicker } from "../../../../component/FilePicker";
import {
  provideCreateCategoryUseCase,
  provideDeleteCategoryUseCase,
  provideGetCategoryUseCase,
  provideUpdateCategoryUseCase,
  provideUploadFileUseCase,
} from "../../../../di/UseCasesModule";
import { CustomAlertDialog } from "../../../../component/CustomAlertDialog";
import {
  capitalizeFirstLetters,
  formatEnumLabel,
  getDominantColorOfImage,
  hasEnglishLetters,
} from "../../../../../core/utils/Utils";
import Marquee from "react-fast-marquee";
import { MAIN_FONT_FAMILY } from "../../../../../data/constant/Constants";
import { SchoolType } from "../../../../../domain/model/Category";

export function CategoryDetailsView() {
  const { id } = useParams();
  const viewModel = CategoryDetailsViewModel(
    provideGetCategoryUseCase(),
    provideCreateCategoryUseCase(),
    provideDeleteCategoryUseCase(),
    provideUpdateCategoryUseCase(),
    provideUploadFileUseCase(),
  );
  const { width } = useWindowDimensions();
  const isNewPage = id === ROUTE_NEW;
  const [titleEditable, setTitleEditable] = useState(isNewPage);
  const [showDeleteAlertDialog, setShowDeleteAlertDialog] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isNewPage && id) {
      viewModel.fetchCategory(id);
    }
  }, []);

  const ref = useRef(null);
  const [imageWidth, setImageWidth] = useState();
  useEffect(() => {
    setImageWidth(ref.current ? (ref.current as any).offsetWidth : 0);
  });

  useEffect(() => {
    setImageWidth(ref.current ? (ref.current as any).offsetWidth : 0);
  }, [width]);

  const [bgColor, setBgColor] = useState(COLOR_WHITE);
  useEffect(() => {
    if (!viewModel.category?.imageUrl) return;
    getDominantColorOfImage(viewModel.category?.imageUrl).then((rgb) => {
      if (rgb[0] == 0 && rgb[1] == 0 && rgb[2] == 0) return;
      setBgColor(`rgba(${rgb[0]},${rgb[1]},${rgb[2]}, 0.4)`);
    });
  }, [viewModel.category?.imageUrl]);

  return (
    <Box>
      <CustomAlertDialog
        title={STRINGS_DELETE_CATEGORY}
        description={STRINGS_ARE_YOU_SURE_YOU_WANT_TO_DELETE_THE_CATEGORY}
        open={showDeleteAlertDialog}
        handleNegativeButtonClick={() => setShowDeleteAlertDialog(false)}
        handlePositiveButtonClick={() => {
          viewModel.deleteCategory();
          setShowDeleteAlertDialog(false);
        }}
      />
      <Stack>
        {viewModel.isLoading ? (
          <Stack
            sx={{
              width: "100%",
              height: "90vh",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress sx={{ color: COLOR_LESS_BLACK }} />
          </Stack>
        ) : (
          <>
            <Stack>
              <HeaderView
                isLayoutFullWidthInSmallerScreens={!isNewPage}
                title={STRINGS_CATEGORY}
                alignItems="start"
                titleLayout={
                  <>
                    {viewModel.category?._id && (
                      <Marquee style={{ width: 100 }} delay={2} pauseOnHover>
                        <CustomTextView
                          text={`#${viewModel.category._id}`}
                          fontWeight="300"
                          variant="h6"
                          textColor={COLOR_LIGHT_GRAY}
                        />
                      </Marquee>
                    )}
                  </>
                }
                layout={
                  <Stack
                    alignItems={"center"}
                    justifyContent={!isNewPage && width < 900 ? "start" : "end"}
                    spacing={1}
                    width={"100%"}
                  >
                    <Stack
                      direction={"row"}
                      alignItems={"center"}
                      justifyContent={
                        !isNewPage && width < 900 ? "start" : "end"
                      }
                      spacing={1}
                      width={"100%"}
                    >
                      {!isNewPage && (
                        <Button
                          onClick={() => setShowDeleteAlertDialog(true)}
                          variant="outlined"
                          fullWidth
                          sx={{
                            borderRadius: 22,
                            borderColor: "red",
                            "&:hover": {
                              borderColor: "red",
                            },
                          }}
                        >
                          <Delete fontSize="small" sx={{ color: "red" }} />
                          <CustomTextView
                            text={STRINGS_DELETE}
                            fontWeight="bold"
                            textColor={"red"}
                            fontSize={14}
                            props={{ ml: 1, textTransform: "none" }}
                          />
                        </Button>
                      )}
                      <Button
                        onClick={
                          isNewPage
                            ? viewModel.createCategory
                            : viewModel.saveCategory
                        }
                        variant="outlined"
                        fullWidth={!isNewPage}
                        sx={{
                          borderRadius: 22,
                          borderColor: COLOR_LESS_BLACK,
                          "&:hover": {
                            borderColor: COLOR_LESS_BLACK,
                          },
                        }}
                      >
                        <Save
                          fontSize="small"
                          sx={{ color: COLOR_LESS_BLACK }}
                        />
                        <CustomTextView
                          text={isNewPage ? STRINGS_CREATE : STRINGS_SAVE}
                          fontWeight="bold"
                          fontSize={14}
                          props={{ ml: 1, textTransform: "none" }}
                        />
                      </Button>
                    </Stack>
                  </Stack>
                }
              />

              <Grid container>
                <Grid size={12}>
                  <Box>
                    <Box p={3}>
                      {/* <TitleView title={"Details"} size="h5" /> */}
                      <Box ref={ref}>
                        {/* <AutoHeight> */}
                        <Stack>
                          <Zoom in={viewModel.category != undefined}>
                            <Stack
                              height={400}
                              visibility={
                                viewModel.category?._id != undefined
                                  ? true
                                    ? "visible"
                                    : "hidden"
                                  : "visible"
                              }
                              position={"relative"}
                            >
                              {viewModel.category?.imageUrl &&
                                viewModel.category.imageUrl.length > 0 && (
                                  <Box
                                    position={"absolute"}
                                    zIndex={3}
                                    width={imageWidth ? imageWidth : "100%"}
                                  >
                                    <Stack
                                      width={"100%"}
                                      direction={"row"}
                                      alignItems={"center"}
                                      justifyContent={"space-between"}
                                      padding={1}
                                    >
                                      <Button
                                        sx={{
                                          background: GRADIENT_GRAY,
                                          border: "solid 1px #000",
                                          borderRadius: 12,
                                          p: 1,
                                        }}
                                      >
                                        <FilePicker
                                          handleFilePicked={
                                            viewModel.setImageUrl
                                          }
                                          layout={
                                            <Stack
                                              direction={"row"}
                                              alignItems={"center"}
                                              spacing={1}
                                            >
                                              <CustomTextView
                                                text="Change Image"
                                                textColor={COLOR_BLACK}
                                                props={{
                                                  textTransform: "none",
                                                }}
                                                fontWeight="bold"
                                                fontSize={14}
                                              />
                                              <Attachment
                                                fontSize="small"
                                                sx={{ color: COLOR_BLACK }}
                                              />
                                            </Stack>
                                          }
                                          acceptType="image/*"
                                        />
                                      </Button>
                                    </Stack>
                                  </Box>
                                )}

                              <Box
                                width={imageWidth ? imageWidth : "100%"}
                                alignItems={"center"}
                                justifyContent={"center"}
                                display={"flex"}
                              >
                                {viewModel.category?.imageUrl &&
                                viewModel.category.imageUrl.length > 0 ? (
                                  <Stack
                                    width={imageWidth ? imageWidth : "100%"}
                                  >
                                    <CustomImageView
                                      width={imageWidth ? imageWidth : "100%"}
                                      height={400}
                                      onLoaded={() => {
                                        setImageLoaded(true);
                                      }}
                                      src={viewModel.category.imageUrl}
                                      scale="contain"
                                      props={{
                                        background: bgColor,
                                        borderRadius: 8,
                                        border: imageLoaded
                                          ? `solid 1px ${COLOR_LIGHT_GRAY}`
                                          : undefined,
                                      }}
                                    />
                                  </Stack>
                                ) : (
                                  <Stack
                                    sx={{
                                      width: "100%",
                                      height: 400,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      border: "solid 1px #000",
                                      borderRadius: 8,
                                    }}
                                  >
                                    <div>
                                      <FilePicker
                                        handleFilePicked={viewModel.setImageUrl}
                                        layout={
                                          <CloudUploadRounded
                                            sx={{
                                              color: COLOR_BLACK,
                                              width: 200,
                                              height: 200,
                                            }}
                                          />
                                        }
                                        acceptType="image/*"
                                      />
                                    </div>
                                  </Stack>
                                )}
                              </Box>
                            </Stack>
                          </Zoom>
                          <Stack sx={{ mt: 2 }}>
                            <CustomTitledView
                              title={"Details"}
                              layout={
                                <Grid container spacing={2} p={2}>
                                  <Grid size={{ xs: 12, lg: 8 }}>
                                    <Stack spacing={1}>
                                      <CustomTitledView
                                        title={STRINGS_TITLE}
                                        background={COLOR_WHITE}
                                        editIcon={
                                          titleEditable ? (
                                            <CheckCircle
                                              fontSize="medium"
                                              sx={{
                                                color: COLOR_LESS_BLACK,
                                              }}
                                            />
                                          ) : (
                                            <EditRounded
                                              fontSize="medium"
                                              sx={{
                                                color: COLOR_LESS_BLACK,
                                              }}
                                            />
                                          )
                                        }
                                        onEditButtonClicked={() => {
                                          if (
                                            viewModel.category?.title &&
                                            viewModel.category.title.length ===
                                              0
                                          )
                                            return;
                                          setTitleEditable(!titleEditable);
                                        }}
                                        layout={
                                          <Stack p={2}>
                                            {titleEditable ? (
                                              <CustomTextField
                                                value={
                                                  viewModel.category?.title
                                                }
                                                fontSize={26}
                                                textColor={COLOR_LESS_BLACK}
                                                multiLine
                                                backgroundColor="transparent"
                                                placeholder={STRINGS_EDIT_TITLE}
                                                variant="standard"
                                                onTextChange={
                                                  viewModel.setTitle
                                                }
                                                sxProps={{
                                                  direction: hasEnglishLetters(
                                                    viewModel.category?.title ??
                                                      "",
                                                  )
                                                    ? "ltr"
                                                    : "rtl",
                                                }}
                                              />
                                            ) : (
                                              <CustomTextView
                                                text={viewModel.category?.title}
                                                fontSize={26}
                                                props={{
                                                  direction: hasEnglishLetters(
                                                    viewModel.category?.title ??
                                                      "",
                                                  )
                                                    ? "ltr"
                                                    : "rtl",
                                                }}
                                              />
                                            )}
                                          </Stack>
                                        }
                                      />
                                    </Stack>
                                  </Grid>
                                  <Grid size={{ xs: 12, lg: 4 }}>
                                    <CustomTitledView
                                      title={"School Type"}
                                      background={COLOR_WHITE}
                                      layout={
                                        <>
                                          {viewModel.category?.schoolType && (
                                            <Select
                                              sx={{
                                                fontFamily: MAIN_FONT_FAMILY,
                                                borderRadius: 12,
                                                m: 2,
                                              }}
                                              inputProps={{
                                                MenuProps: {
                                                  PaperProps: {
                                                    sx: {
                                                      borderRadius: 4,
                                                    },
                                                  },
                                                },
                                              }}
                                              value={
                                                viewModel.category?.schoolType
                                              }
                                              onChange={(e) => {
                                                viewModel.setSchoolType(
                                                  e.target.value,
                                                );
                                              }}
                                            >
                                              {Object.values(SchoolType).map(
                                                (type) => {
                                                  return (
                                                    <MenuItem
                                                      key={type}
                                                      sx={{
                                                        fontFamily:
                                                          MAIN_FONT_FAMILY,
                                                      }}
                                                      value={type}
                                                    >
                                                      {formatEnumLabel(type)}
                                                    </MenuItem>
                                                  );
                                                },
                                              )}
                                            </Select>
                                          )}
                                        </>
                                      }
                                    />
                                  </Grid>
                                </Grid>
                              }
                            />
                          </Stack>
                        </Stack>
                        {/* </AutoHeight> */}
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Stack>
          </>
        )}
      </Stack>
    </Box>
  );
}
